import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import Digits from '../Digits';
import ReCaptcha from '../ReCaptcha';
import devices from '../../styles/devices';
import { VRPAT, JVCEF } from '~/config/constants';

const Form = styled.form`
  ${({ issuer }) =>
    issuer === JVCEF &&
    css`
      margin-top: 100px;

      @media (min-width: ${devices?.desktop}) {
        margin-top: 0;
      }
    `}
`;

const Title = styled.h1`
  position: relative;
  font-size: 1.625rem;
  color: ${({ theme }) => theme.colors.white};
  line-height: 33px;
  font-weight: 700;
  z-index: 1;

  ${({ issuer }) =>
    issuer === VRPAT &&
    css`
      &:before {
        content: '';
        position: absolute;
        left: 0;
        top: -100px;
        height: 200px;
        width: 100%;
        z-index: -1;
        background: linear-gradient(
          180deg,
          rgb(255 255 255 / 0%) 0%,
          rgba(0, 170, 19, 1) 100%
        );
      }
    `}

  small {
    font-size: 1rem;
    font-weight: 600;
  }

  @media (min-width: ${devices?.desktop}) {
    line-height: 38px;

    &:before {
      content: none;
    }
  }
`;
const TitleContainer = styled.div`
  padding-left: 20px;
  height: 100px;
  flex-direction: column;
  justify-content: center;
  border-left: 20px solid ${({ theme }) => theme.colors.secondary};

  @media (min-width: ${devices?.desktop}) {
    height: auto;
  }
`;
const Description = styled.p`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.white};
  max-width: 525px;
  line-height: 36px;
  font-weight: 500;
  margin-left: 20px;
  padding-right: 15px;

  span {
    font-weight: 700;
  }

  @media (min-width: ${devices?.desktop}) {
    margin-left: 40px;
    font-weight: 600;
  }
`;
const OnlyMobile = styled(TitleContainer)`
  display: flex;

  @media (min-width: ${devices?.desktop}) {
    display: none;
  }
`;
const OnlyDesktop = styled(TitleContainer)`
  display: none;

  @media (min-width: ${devices?.desktop}) {
    display: flex;
  }
`;
const ContainerActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 40px;

  @media (min-width: ${devices?.desktop}) {
    margin: 100px 0 0 60px;
    width: 302px;
  }
`;
const ContainerReCaptcha = styled.div`
  height: 76px;
  margin-top: 20px;
`;

export default function ValidateCardForm({ reCaptchaKey, submit, issuer }) {
  const [hasInputChanged, setHasInputChanged] = useState(false);
  const [inputsValues, setInputsValues] = useState({});
  const [successKey, setSuccessKey] = useState(false);
  const [inputFocus, setInputFocus] = useState('');
  const formattedCompanyName = issuer?.company.replace('Benefícios', '');

  function setValues(obj) {
    const { key, value } = obj;

    setInputsValues({
      ...inputsValues,
      [key]: value,
    });
  }

  function resetValues() {
    setInputFocus('digit_1');
    setInputsValues({});

    setTimeout(() => {
      setInputFocus('');
    });
  }

  useEffect(() => {
    const qtyValues = Object.values(inputsValues).filter(key => key);

    if (qtyValues.length === 4 && successKey) {
      submit(qtyValues.join(''));
      resetValues();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successKey, inputsValues]);

  useEffect(() => {
    Object.entries(inputsValues).forEach(([key, value]) => {
      if (hasInputChanged) {
        setValues({ key, value });
        setHasInputChanged(false);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputsValues]);

  function handleDigitsOnChange(objValue) {
    setValues(objValue);
    setHasInputChanged(true);
  }

  return (
    <Form issuer={issuer?.name} noValidate>
      <OnlyMobile>
        <Title issuer={issuer?.name}>
          <small>Seja bem-vindo</small>
          <br /> ao novo portal {formattedCompanyName}.
        </Title>
      </OnlyMobile>

      <OnlyDesktop>
        <Title>
          Seja bem-vindo ao novo
          <br /> portal da {issuer?.company}.
        </Title>
      </OnlyDesktop>

      <Description>
        Para que você tenha acesso às informações do seu benefício, digite os
        <span> últimos 4 dígitos</span> de um dos seus cartões{' '}
        {formattedCompanyName}.
      </Description>
      <ContainerActions>
        <Digits
          forceFocus={inputFocus}
          onChange={objValue => handleDigitsOnChange(objValue)}
          type="tel"
          values={inputsValues}
          label="Informe o 4 últimos dígitos do cartão"
          color="white"
          // Passando "tel" como type, pois o tipo "number" fez com que o forms parasse de
          // escutar a variável as alterações nos campos, deixando de passar o valor digitado para o watch.
          // E "tel" serve para forçar o teclado numérico nos aparelhos portáteis.
        />
        <ContainerReCaptcha>
          {reCaptchaKey && (
            <ReCaptcha
              data-testid="captcha"
              onChange={successKey => setSuccessKey(successKey)}
              sitekey={reCaptchaKey}
            />
          )}
        </ContainerReCaptcha>
      </ContainerActions>
    </Form>
  );
}

ValidateCardForm.propTypes = {
  submit: PropTypes.func,
  reCaptchaKey: PropTypes.string,
  issuer: PropTypes.shape({
    company: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
};
